import Component from "./Component.js"
import Container from "./Container.js"
import PanelControl from "./PanelControl.js"
import PlayPause from "./PlayPause.js"
import PauseOverlay from "./PauseOverlay.js"
import BufferingOverlay from "./BufferingOverlay.js"
import CurrentTime from "./CurrentTime.js"
import Live from "./Live.js"
import Duration from "./Duration.js"
import Progress from "./Progress.js"
import Volume from "./Volume.js"
import Mute from "./Mute.js"
import Fullscreen from "./Fullscreen.js"
import SettingsButton from "./SettingsButton.js"
import SettingsPanel from "./SettingsPanel.js"
import ShareButton from "./ShareButton.js"
import SharePanel from "./SharePanel.js"
import CaptionToggleButton from "./CaptionToggleButton.js"
import JumpBack from "./JumpBack.js"
import JumpForward from "./JumpForward.js"
import Poster from "./Poster.js"
import Title from "./Title.js"
import ErrorMsg from "./ErrorMsg.js"
import Utils from "./Utils.js"
import ContextMenu from "./ContextMenu.js"
import Ads from "./Ads.js"
import Airplay from "./Airplay.js"
import PiP from "./PiP.js"
import ChromeCast from "./ChromeCast.js"
import AudioTracks from "./AudioTracks.js"
import Mode from "./Mode.js"
import ActiveState from "./ActiveState.js"
import Panel from "./Panel.js"
import Unmute from "./Unmute.js"

export default class UI extends Component {

  constructor(props) {
    super(props)
    this.feature = "ui"
    this.state.playState = "ready"
    this.state.open = false
    this.state.dragging = false
    this.state.mode = this.config.mode
    this.isTouchDevice = akamai.amp.Utils.isTouch
    this.contextMenuClick = this.contextMenuClick.bind(this)
    this.autoHide = (this.config.autoHide != null) ? this.config.autoHide : 3
    this.timeout = null

    const dispatcher = new akamai.amp.EventDispatcher(this)
    for (let key in dispatcher) {
      let value = dispatcher[key]
      if (typeof value == "function") {
        this[key] = value.bind(dispatcher)
      }
    }
    this.dispatcher = dispatcher

    this.bindHandlers(["onMouseEnter", "onMouseLeave", "onFocus", "onBlur", "onKeyDown", "onContextMenu", "onClick"])
  }

  getChildContext() {
    return {
      player: this.props.player,
      config: this.props.config,
      plugin: this
    }
  }

  get adMode() {
    return (this.config.adMode != null) ? this.config.adMode : Mode.AUTO
  }

  get debug() {
    return (this.config.debug != null) ? this.config.debug : this.player.config.debug
  }

  get container() {
    return this.refs.container
  }

  get ads() {
    return this.refs.ads
  }

  get components() {
    return this.refs
  }

  get controls() {
    return this.refs.controls
  }

  get mode() {
    return this.state.mode || Mode.AUTO
  }

  set mode(value) {
    value = value || Mode.AUTO
    const mode = this.state.mode
    this.container.classList.update({
      [`amp-controls-${mode}`]: false,
      [`amp-controls-${value}`]: true
    })
    this.setState({mode: value})

    if (value === Mode.AUTO) {
      if (this.idleUtil == null && !this.isTouchDevice) {
        this.idleUtil = new akamai.amp.IdleUtil(this.player.viewComponent, this.autoHide * 1000)
        this.idleUtil.addEventListener("activechange", (event) => {
          const inactive = (this.activeState == ActiveState.INACTIVE) ? ActiveState.IDLE : ActiveState.INACTIVE
          this.activeState = (event.detail.active !== true) ? inactive : ActiveState.ACTIVE
        })
      }
      if (this.idleUtil != null) {
        this.idleUtil.start()
      }
    }
    else {
      if (this.idleUtil != null) {
        this.idleUtil.stop()
      }
    }

    // TODO: Remove these when controls plugin is completely removed from amp-web
    this.player.container.classList.remove(`amp-controls-${mode}`)
    this.player.container.classList.add(`amp-controls-${value}`)
  }

  get dragging() {
    return this.state.dragging
  }

  set dragging(value) {
    if (value == true) {
      this.stopIdleTimeout()
    }
    else {
      this.startIdleTimeout()
    }
    this.container.classList.update({[`amp-dragging`]: value})
    this.setState({dragging: value})
  }

  get open() {
    return this.state.open
  }

  set open(value) {
    this.container.classList.update({[`amp-open`]: value})
    this.setState({open: value})
  }

  onready(event) {

    if (this.config.mode != null)
      this.mode = this.config.mode

    this.container.classList.add(`amp-${this.player.mode}`)
    this.container.classList.update({
      "amp-cc-active": this.player.captioning && !this.player.captioning.hidden,
      "amp-touch": this.isTouchDevice
    })

    if (this.player.captioning != null) {
      this.player.captioning.addEventListener("visibilitychange", this.onvisibilitychange.bind(this))
    }

    this.eventType = (window.PointerEvent != null) ? "pointer" : "mouse"
    this.player.container.addEventListener(`${this.eventType}enter`, this.onMouseEnter)
    this.player.container.addEventListener(`${this.eventType}leave`, this.onMouseLeave)

    // Workaround for UI flicker and FOUT
    setTimeout(() => {
      this.container.element.style.display = "block"
    }, 10)
  }

  ondestroy() {
    this.stopIdleTimeout()
    this.player.container.removeEventListener(`${this.eventType}enter`, this.onMouseEnter)
    this.player.container.removeEventListener(`${this.eventType}leave`, this.onMouseLeave)
  }

  onvisibilitychange(event) {
    this.container.classList.update({"amp-cc-active": event.detail})
  }

  onplaybacktargetchange(event) {
    this.container.classList.update({"amp-remote-playback": (event.detail.value != "amp")})
  }

  onmediachange(event) {
    const media = this.player.media
    this.container.classList.update({"amp-audio": media.medium == "audio"})
    this.container.classList.update({"amp-text-tracks": media.tracks && media.tracks.length})
  }

  onmutemouseover(event) {
  	this.stopIdleTimeout()
  	this.refs.volume.open = true
  }

  onmutemouseout(event) {
  	this.stopIdleTimeout()
  	this.timeout = setTimeout(() => {
      this.refs.volume.open = false
    }, 350)
  }

  onvolumemouseover(event) {
  	this.stopIdleTimeout()
  }

  onfullscreenchange(event) {
    this.container.classList.update({"amp-full-screen": event.detail})
  }

  onmutechange(event) {
    this.container.classList.update({"amp-muted": event.detail})
  }

  onplaystatechange(event) {
    const updates = {
      [`amp-waiting`]: false,
      [`amp-${event.detail.previous}`]: false,
      [`amp-${event.detail.value}`]: true
    }
    this.container.classList.update(updates)
  }

  // onmediasequenceinitialized(event) {
  //   const detail = event.detail
  //   if (detail.autoplay === true && detail.muted === true) {
  //     const container = this.player.getContainer()
  //     const unmute = (event) => {
  //       if (!event.target.classList.contains("amp-mute")) {
  //         this.player.muted = false
  //       }
  //       container.removeEventListener("click", unmute)
  //     }
  //     container.addEventListener("click", unmute)
  //   }
  // }

  oncanplaythrough(event) {
    this.container.classList.update({"amp-can-play": true})
  }

  onplaying(event) {
    this.container.classList.update({"amp-waiting": false})
    this.container.classList.remove("amp-qualitychange-manual")
    this.container.classList.remove("amp-qualitychange-auto")
    this.startIdleTimeout()
  }

  onpause(event) {
    this.stopIdleTimeout()
  }

  onwaiting(event) {
    this.container.classList.update({"amp-waiting": true})
    this.player.once("timeupdate", () => {
      if (this.player.seeking === false) {
        this.container.classList.update({"amp-waiting": false})
      }
    })
  }

  onbusy(event) {
    this.container.classList.update({"amp-busy": event.detail})
  }

  onseeking(event) {
    this.container.classList.update({"amp-waiting": true})
  }

  onseeked(event) {
    this.container.classList.update({"amp-waiting": false})
  }

  onerror(event) {
    this.container.classList.update({"amp-error": true})
  }

  onislive(event) {
    this.container.classList.update({"amp-is-live": event.detail})
  }

  ontemporaltypechange(event) {
    const updates = {
      [`amp-${event.detail.previous}`]: false,
      [`amp-${event.detail.value}`]: true
    }
    this.container.classList.update(updates)
  }

  onqualitychanging(event) {
    this.container.classList.add(`amp-qualitychange-${this.player.qualityMode}`)
  }

  onqualitychange(event) {
    this.container.classList.remove(`amp-qualitychange-${this.player.qualityMode}`)
  }

  onqualityswitched(event) {
    this.onqualitychange(event)
  }

  togglePlayPause() {
    if (this.player.playState == "ready" || this.player.paused) {
      this.player.play()
    } else {
      this.player.pause()
    }
  }

  set activeState(value) {
    const active = (value == ActiveState.ACTIVE)
    const inactive = (value == ActiveState.INACTIVE)
    const idle = (value == ActiveState.IDLE)
    this.container.classList.update({"amp-active": active, "amp-inactive": inactive, "amp-idle": idle})

    // TODO: Remove these when controls plugin is completely removed from amp-web
    let action = active ? "add" : "remove"
    this.player.container.classList[action]("amp-active")

    action = inactive ? "add" : "remove"
    this.player.container.classList[action]("amp-inactive")

    if (inactive === true) {
      for (let key in this.refs) {
        const value = this.refs[key]
        if (value instanceof Panel && value.open === true) {
          value.open = false
        }
      }
      this.startIdleTimeout()
    }
  }

  get activeState() {
    switch (true) {
      case this.container.classList.contains("amp-active"):
        return ActiveState.ACTIVE

      case this.container.classList.contains("amp-inactive"):
        return ActiveState.INACTIVE

      default:
        return ActiveState.IDLE
    }
  }

  get adInProgress() {
    return (this.player.ads && this.player.ads.inProgress)
  }

  startIdleTimeout() {
    if(this.refs.settingsPanel.open) {
      this.refs.settingsPanel.close()
      this.refs.volume.open = false
    }
    this.stopIdleTimeout()
    this.timeout = setTimeout(() => {
      this.activeState = ActiveState.IDLE
    }, this.autoHide * 1000)
  }

  stopIdleTimeout() {
    clearTimeout(this.timeout)
  }

  isNotMouseEvent(event) {
    return (event.pointerType == null && this.isTouchDevice) || (event.pointerType != null && event.pointerType != "mouse")
  }

  onMouseEnter(event) {
    if (this.isNotMouseEvent(event))
      return

    this.stopIdleTimeout()
    this.activeState = ActiveState.ACTIVE
  }

  onMouseLeave(event) {
    if (this.isNotMouseEvent(event))
      return

    const element = document.elementFromPoint(event.clientX, event.clientY)
    if (this.player.playState == "ready" || this.open || this.player.container.contains(element))
      return

    if (this.dragging) {
      const handler = () => {
        window.removeEventListener("mouseup", handler)
        if (!akamai.amp.Utils.isMouseOverElement(this.container.element))
          this.onMouseLeave(event)
      }
      window.addEventListener("mouseup", handler)
      return
    }

    this.activeState = ActiveState.INACTIVE
  }

  onClick() {
    if (this.isTouchDevice) {
      if (this.activeState == ActiveState.ACTIVE) {
        this.activeState = ActiveState.IDLE
        this.stopIdleTimeout()
      }
      else {
        this.activeState = ActiveState.ACTIVE
        this.startIdleTimeout()
      }
    }
    else {
      this.togglePlayPause()
    }
  }

  isFocused(element) {
    const container = this.container.element
    return (element == container || container.contains(element))
  }

  onFocus(event) {
    const focused = this.isFocused(event.target)
    this.focused = (event.target.nodeName != "BUTTON" && focused)
    if (focused)
      this.onMouseEnter(event)
  }

  onBlur(event) {
    const focused = this.isFocused(event.target)
    this.focused = (event.target.nodeName != "BUTTON" && focused)
    if (!focused)
      this.onMouseLeave(event)
  }

  onKeyDown(event) {
    switch (event.keyCode) {
      case 77: // M
        this.player.muted = !this.player.muted
      break;

      case 67: // C
          this.player.captioning.hidden = !this.player.captioning.hidden
      break;

      case 70: // F
        this.player.displayState == this.player.displayState == 'full-screen'? this.player.exitFullScreen() : this.player.enterFullScreen()
      break;
    }

    if (this.focused == false)
      return

    switch (event.keyCode) {
      case 13: // Enter
      case 32: // Space
        this.togglePlayPause()
      break;

      case 39: // Left
      case 37: // Right
        if (this.refs.volume.focused === false)
          this.refs.progress.onKeyDown(event)
      break;

      case 38: // Up
      case 40: // Down
        if (this.refs.progress.focused === false)
          this.refs.volume.onKeyDown(event)
      break;
    }
  }

  contextMenuClick(event) {
    if (/selectable/.test(event.target.className))
      return false
    this.removeContextMenu(event)
    return false
  }

  removeContextMenu(event) {
    document.body.removeEventListener("click", this.contextMenuClick)
    const parent = document.body
    parent.removeChild(this.contextMenu)
    this.contextMenu = null
  }

  onContextMenu(event) {
    if (this.debug === true)
      return

    event.preventDefault()

    const parent = document.body
    const contextMenu = (
      <ContextMenu player={this.player} x={event.pageX} y={event.pageY} />
    )

    if (this.contextMenu != null)
      this.removeContextMenu()

    this.contextMenu = document.createElement("div")
    parent.appendChild(this.contextMenu)
    document.body.addEventListener("click", this.contextMenuClick)
    ReactDOM.render(contextMenu, this.contextMenu)
  }

  replay() {
    this.player.replay()
  }

  render() {
    return (
      <Container ref="container"
          tabIndex="0"
          style={{display: "none"}}
          classList={["amp-react", "amp-ui", "amp-ready", "amp-active"]}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          onContextMenu={this.onContextMenu}
          >
        <div className="amp-interactive" onClick={this.onClick} />
        <Poster ref="poster" />
        <Title ref="title" />
        <Ads ref="ads" />
        <PauseOverlay ref="pauseOverlay" />
        <Unmute ref="unmute" />
        <BufferingOverlay ref="bufferingOverlay" />
        <Progress ref="progress" />
        <Container ref="controls" className="amp-controls amp-bar">
          <PlayPause ref="playpause" />
          <JumpBack ref="jumpback"/>
          <JumpForward ref="jumpforward"/>
          <div ref="timeDisplay" className="amp-time-display">
             <CurrentTime ref="currentTime" />
             <span ref="timeSeparator" className="amp-time-separator" />
             <Duration ref="duration" />
             <Live ref="live" />
          </div>
          <Mute ref="mute" />
          <Volume ref="volume" />
          <div ref="spacerLeft" className="amp-spacer" />
          <div ref="spacerRight" className="amp-spacer" />
          <ShareButton ref="share" panel="sharePanel" />
          <CaptionToggleButton ref="cc" />
          <SettingsButton ref="settings" panel="settingsPanel" />
          <PiP ref="pip" />
          <Airplay ref="airplay" />
          <ChromeCast ref="chromecast" />
          <Fullscreen ref="fullscreen" />
        </Container>
        <SharePanel ref="sharePanel" />
        <SettingsPanel ref="settingsPanel" />
        <ErrorMsg ref="error" />
        {this.state.contextMenu}
      </Container>
    )
  }

  destroy() {
    ReactDOM.unmountComponentAtNode(this._container)
  }

  static get childContextTypes() {
    return {
      player: PropTypes.object,
      config: PropTypes.object,
      plugin: PropTypes.object
    }
  }

  static create(player, config, key) {
    if (config.native === true) {
      const ui = {config}
      player.once("ready", () => player.mediaElement.controls = true)
      if (player.ads != null) {
        ui.ads = {
          container: document.createElement("div")
        }
        player.container.appendChild(ui.ads.container)
      }
      return Promise.resolve(ui)
    }

    return new Promise((resolve, reject) => {
      player.once("ready", () => {
        if (player.mediaElement.setPlayerProperty != null)
          player.mediaElement.setPlayerProperty("controlsMode", "none")
      })

      const ui = ReactDOM.render(<UI player={player} config={config} />, player.container)
      player.adContainer = ui.ads.container
      resolve(ui)
    })
  }
}
